import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  FileSpreadsheet, 
  Download, 
  Loader2,
  AlertCircle,
  Database,
  Columns,
  Users,
  CheckCircle,
  ArrowRight,
  RefreshCw,
  Info
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const colors = {
  dark: '#0D2440',
  blue: '#2E5E99',
  gray: '#5A7A9B',
  border: '#C8D8E8',
  bg: '#E7F0FA',
};

export default function CsvTransformer() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { isDataLoaded, dataStatus } = useData();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  // Set up axios auth header
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [token]);

  const fetchStats = useCallback(async () => {
    if (!isDataLoaded) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/services/csv-transformer/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Fehler beim Laden der Statistiken');
    } finally {
      setLoading(false);
    }
  }, [isDataLoaded]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleDownload = async (transformType) => {
    setDownloading(transformType);
    
    try {
      const endpoint = transformType === 'clean' 
        ? `${API}/services/csv-transformer/transform/clean-columns`
        : `${API}/services/csv-transformer/transform/team-columns`;
      
      const response = await axios.post(endpoint, {}, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = transformType === 'clean' ? 'cleaned_columns.csv' : 'team_columns.csv';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV-Datei erfolgreich heruntergeladen');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Fehler beim Herunterladen der CSV-Datei');
    } finally {
      setDownloading(null);
    }
  };

  if (!isDataLoaded) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="py-8">
            <div className="flex flex-col items-center text-center">
              <Database className="h-12 w-12 text-amber-500 mb-4" />
              <h3 className="text-lg font-semibold text-amber-800 mb-2">
                Kein Datenstand geladen
              </h3>
              <p className="text-amber-700 mb-4">
                Bitte laden Sie zuerst einen Datenstand hoch, um den CSV Transformer zu nutzen.
              </p>
              <Button onClick={() => navigate('/dashboard')} style={{ background: colors.blue }}>
                Zum Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: colors.dark }}>
          <FileSpreadsheet className="h-7 w-7" style={{ color: colors.blue }} />
          CSV Transformer
        </h1>
        <p className="text-sm mt-1" style={{ color: colors.gray }}>
          Transformieren Sie die Periods-Daten aus dem Datenstand in verschiedene CSV-Formate
        </p>
      </div>

      {/* Data Status */}
      <Card className="mb-6 bg-green-50 border-green-200">
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <div>
              <p className="font-medium text-green-800">
                Datenstand geladen: {dataStatus?.system_name || 'Unbekannt'}
              </p>
              <p className="text-sm text-green-700">
                {dataStatus?.periods_count || 0} Einträge verfügbar
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistics */}
      {loading ? (
        <Card className="mb-6">
          <CardContent className="py-8">
            <div className="flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin" style={{ color: colors.blue }} />
              <span className="ml-2" style={{ color: colors.gray }}>Lade Statistiken...</span>
            </div>
          </CardContent>
        </Card>
      ) : stats && (
        <Card className="mb-6 bg-white" style={{ borderColor: colors.border }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2" style={{ color: colors.dark }}>
              <Info className="h-5 w-5" style={{ color: colors.blue }} />
              Daten-Übersicht
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 rounded-lg" style={{ background: colors.bg }}>
                <div className="text-2xl font-bold" style={{ color: colors.dark }}>{stats.total_rows}</div>
                <div className="text-xs" style={{ color: colors.gray }}>Zeilen</div>
              </div>
              <div className="text-center p-3 rounded-lg" style={{ background: colors.bg }}>
                <div className="text-2xl font-bold" style={{ color: colors.dark }}>{stats.team_columns}</div>
                <div className="text-xs" style={{ color: colors.gray }}>Team-Spalten</div>
              </div>
              <div className="text-center p-3 rounded-lg" style={{ background: colors.bg }}>
                <div className="text-2xl font-bold" style={{ color: colors.dark }}>{stats.columns_with_colon}</div>
                <div className="text-xs" style={{ color: colors.gray }}>Spalten mit ":"</div>
              </div>
              <div className="text-center p-3 rounded-lg" style={{ background: colors.bg }}>
                <div className="text-2xl font-bold" style={{ color: colors.dark }}>{stats.max_teams_per_user}</div>
                <div className="text-xs" style={{ color: colors.gray }}>Max. Teams/User</div>
              </div>
            </div>
            
            {stats.top_teams && stats.top_teams.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium mb-2" style={{ color: colors.dark }}>Top Teams:</p>
                <div className="flex flex-wrap gap-2">
                  {stats.top_teams.slice(0, 8).map(([team, count], idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      {team}: {count}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Transformation Options */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Option 1: Clean Columns */}
        <Card className="bg-white" style={{ borderColor: colors.border }}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ color: colors.dark }}>
              <Columns className="h-5 w-5" style={{ color: colors.blue }} />
              Option 1: Spalten bereinigen
            </CardTitle>
            <CardDescription>
              Entfernt alles nach dem ":" in den Team-Spaltennamen und -werten
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 rounded-lg" style={{ background: colors.bg }}>
              <p className="text-sm font-medium mb-2" style={{ color: colors.dark }}>Vorher:</p>
              <code className="text-xs block mb-3 p-2 bg-white rounded" style={{ color: colors.gray }}>
                "Produktion:Abteilung A" → "x"
              </code>
              
              <div className="flex items-center justify-center my-2">
                <ArrowRight className="h-5 w-5" style={{ color: colors.blue }} />
              </div>
              
              <p className="text-sm font-medium mb-2" style={{ color: colors.dark }}>Nachher:</p>
              <code className="text-xs block p-2 bg-white rounded" style={{ color: colors.gray }}>
                "Produktion" → "x"
              </code>
            </div>
            
            <Button 
              onClick={() => handleDownload('clean')}
              disabled={downloading === 'clean'}
              className="w-full"
              style={{ background: colors.dark }}
            >
              {downloading === 'clean' ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Wird erstellt...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  CSV herunterladen
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Option 2: Team Columns */}
        <Card className="bg-white" style={{ borderColor: colors.border }}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ color: colors.dark }}>
              <Users className="h-5 w-5" style={{ color: colors.blue }} />
              Option 2: Team-Spalten
            </CardTitle>
            <CardDescription>
              Wandelt "x"-Markierungen in benannte Team-Spalten um
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 rounded-lg" style={{ background: colors.bg }}>
              <p className="text-sm font-medium mb-2" style={{ color: colors.dark }}>Vorher:</p>
              <code className="text-xs block mb-3 p-2 bg-white rounded" style={{ color: colors.gray }}>
                Alle: "x" | Produktion: "x" | Büro: ""
              </code>
              
              <div className="flex items-center justify-center my-2">
                <ArrowRight className="h-5 w-5" style={{ color: colors.blue }} />
              </div>
              
              <p className="text-sm font-medium mb-2" style={{ color: colors.dark }}>Nachher:</p>
              <code className="text-xs block p-2 bg-white rounded" style={{ color: colors.gray }}>
                Team 1: "Alle" | Team 2: "Produktion"
              </code>
            </div>
            
            <div className="text-xs p-2 rounded border" style={{ borderColor: colors.border, color: colors.gray }}>
              <strong>Hinweis:</strong> Teams werden alphabetisch sortiert, sodass die Reihenfolge für alle Benutzer konsistent ist.
            </div>
            
            <Button 
              onClick={() => handleDownload('teams')}
              disabled={downloading === 'teams'}
              className="w-full"
              style={{ background: colors.dark }}
            >
              {downloading === 'teams' ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Wird erstellt...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  CSV herunterladen
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Refresh Button */}
      <div className="mt-6 text-center">
        <Button 
          variant="outline" 
          onClick={fetchStats}
          disabled={loading}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Statistiken aktualisieren
        </Button>
      </div>
    </div>
  );
}
